/**
 * /style-guide — Internal design system reference.
 *
 * Renders every custom CSS class, design token, and reusable component on a
 * single page. Use it to:
 *   • Spot missing utility classes (like the original .input-base bug)
 *   • QA visual regressions across the app
 *   • Hand off to designers / new contributors
 *
 * Public route (no auth) — safe because nothing here hits the API.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, XCircle, ArrowLeft, Wrench, Plus, Copy } from "@phosphor-icons/react";
import { DIM_COLORS } from "@/components/r3d/scene";

// Mock validation payload (mirrors the live server output)
const MOCK_REPORT = {
  compliant: false,
  summary: { items_placed: 7, items_unplaced: 2, errors: 1, warnings: 2 },
  metadata: { code_reference: "NEC 110.26(A) / 110.26(E)", working_space_in: 36, headroom_required_in: 78 },
  issues: [
    { code: "WORKING_SPACE", level: "error", tag: "MCC-001", message: "MCC-001 has only 24\" working clearance (need 36\")." },
    { code: "TRAY_FILL",      level: "warning", tag: "S2",      message: "Tray segment S2 fill is 42% (target ≤30%)." },
    { code: "HEADROOM",       level: "warning", tag: "XFMR-201", message: "XFMR-201 top-of-equipment within 6\" of ceiling." },
    { code: "INFO",           level: "info",    tag: null,       message: "All conductors sized per NEC 310.16 (Copper)." },
  ],
};

/** Copyable mono-formatted chip — hex codes, class names, design tokens. */
function CopyChip({ value, label = null, kind = "token" }) {
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${kind === "hex" ? "Hex" : "Class"} ${value} copied`);
    } catch {
      toast.error("Clipboard unavailable");
    }
  };
  return (
    <button
      onClick={onCopy}
      className="group inline-flex items-center gap-1 font-mono text-xs text-gray-400 hover:text-[#00E5FF] transition-colors"
      data-testid={`sg-copy-${value}`}
      title="Click to copy"
    >
      <span>{label || value}</span>
      <Copy size={11} className="opacity-40 group-hover:opacity-100" />
    </button>
  );
}

function Section({ title, slug, children }) {
  return (
    <section id={slug} className="mb-16 scroll-mt-12">
      <div className="label-tag mb-2">// {slug}</div>
      <h2 className="font-display text-3xl font-bold mb-6 text-white">{title}</h2>
      {children}
    </section>
  );
}

function Swatch({ name, hex, hint }) {
  return (
    <div className="border border-white/10 p-3 flex items-center gap-3 font-mono text-xs" data-testid={`swatch-${name}`}>
      <div className="w-12 h-12 shrink-0" style={{ background: hex }} />
      <div className="min-w-0">
        <div className="text-white font-bold uppercase tracking-[0.18em]">{name}</div>
        <CopyChip value={hex} kind="hex" />
        {hint && <div className="text-gray-500 text-[10px]">{hint}</div>}
      </div>
    </div>
  );
}

export default function StyleGuide() {
  const [demoSel, setDemoSel] = useState("Ladder");
  const [demoNum, setDemoNum] = useState("");

  return (
    <div className="min-h-screen bg-[#0A0C10] text-gray-200 industrial">
      {/* Topbar mini */}
      <header className="border-b border-white/10 px-6 py-4 flex items-center justify-between sticky top-0 bg-[#0A0C10]/95 backdrop-blur z-10">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white" data-testid="back-to-landing">
          <ArrowLeft size={16} /> back to studio
        </Link>
        <div className="text-right">
          <div className="font-display font-black text-white text-lg tracking-tighter">EEngDeS · Style Guide</div>
          <div className="text-[10px] text-gray-500 uppercase tracking-[0.2em]">Internal design system</div>
        </div>
      </header>

      {/* Index */}
      <nav className="border-b border-white/10 px-6 py-3 flex flex-wrap gap-3 text-xs font-mono text-gray-400" data-testid="style-guide-nav">
        {["colors", "typography", "buttons", "form-controls", "label-tags", "tables", "validation-report", "dim-colors", "patterns"].map((s) => (
          <a key={s} href={`#${s}`} className="hover:text-[#00E5FF] uppercase tracking-[0.15em]">{s.replace(/-/g, " ")}</a>
        ))}
      </nav>

      <main className="px-6 py-10 max-w-6xl mx-auto">

        <Section title="Brand colors" slug="colors">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Swatch name="bg-base"    hex="#0A0C10" hint="page background" />
            <Swatch name="panel"      hex="#0a0d12" hint="cards / tables" />
            <Swatch name="head-row"   hex="#1A1F29" hint="table headers" />
            <Swatch name="accent-cyan" hex="#00E5FF" hint="primary CTA, links" />
            <Swatch name="accent-yel" hex="#FFCC00" hint="warnings, small-power" />
            <Swatch name="accent-red" hex="#FF3B5C" hint="errors" />
            <Swatch name="muted"      hex="#6B7280" hint="label-tag, hints" />
            <Swatch name="text"       hex="#E5E7EB" hint="body copy" />
          </div>
        </Section>

        <Section title="Typography" slug="typography">
          <div className="space-y-3">
            <div><div className="label-tag">font-display (Chivo)</div><div className="font-display text-4xl font-bold">Cables, trays &amp; raceways</div></div>
            <div><div className="label-tag">font-mono (JetBrains Mono)</div><div className="font-mono text-sm">CL24-4-AL · Aluminum · $71.60/ft</div></div>
            <div><div className="label-tag">body</div><div className="text-sm text-gray-400">Body copy uses Chivo at the same weight as buttons. Mono is reserved for SKUs, codes, and numbers.</div></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
              <div><div className="label-tag">H1</div><div className="font-display text-5xl font-bold">H1</div></div>
              <div><div className="label-tag">H2</div><div className="font-display text-3xl font-bold">H2</div></div>
              <div><div className="label-tag">H3</div><div className="font-display text-xl font-bold">H3</div></div>
            </div>
          </div>
        </Section>

        <Section title="Buttons" slug="buttons">
          <div className="flex flex-wrap gap-3 items-center">
            <button className="btn-primary inline-flex items-center gap-2" data-testid="sg-btn-primary"><Plus weight="bold" size={16} /> Primary</button>
            <button className="btn-primary inline-flex items-center gap-2" disabled>Primary disabled</button>
            <button className="btn-ghost inline-flex items-center gap-2" data-testid="sg-btn-ghost"><Wrench size={16} /> Ghost</button>
            <button className="btn-ghost text-xs py-1.5">Ghost small</button>
            <span className="font-mono text-xs text-gray-500 ml-4 inline-flex items-center gap-2">
              <CopyChip value=".btn-primary" /> · <CopyChip value=".btn-ghost" />
            </span>
          </div>
        </Section>

        <Section title="Form controls" slug="form-controls">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-2xl">
            <div>
              <div className="label-tag mb-1">Select</div>
              <select value={demoSel} onChange={(e) => setDemoSel(e.target.value)} className="input-base text-sm" data-testid="sg-select">
                <option>Ladder</option><option>Ventilated Trough</option><option>Solid Bottom</option><option>Wire Mesh</option>
              </select>
            </div>
            <div>
              <div className="label-tag mb-1">Number</div>
              <input type="number" placeholder="0" value={demoNum} onChange={(e) => setDemoNum(e.target.value)} className="input-base text-sm" data-testid="sg-number" />
            </div>
            <div>
              <div className="label-tag mb-1">Text</div>
              <input placeholder="ABB Installation Product" className="input-base text-sm" data-testid="sg-text" />
            </div>
            <div className="md:col-span-3 text-[10px] text-gray-500 font-mono inline-flex items-center gap-2 flex-wrap">
              Inputs all use <CopyChip value=".input-base" /> — single source of truth in
              <span className="text-[#00E5FF]">/app/frontend/src/index.css</span>. The bug fixed in v1.19.2 was caused by this class being referenced but undefined → selected options rendered invisibly.
            </div>
          </div>
        </Section>

        <Section title="Label tags" slug="label-tags">
          <div className="flex flex-wrap gap-6 items-center">
            <div className="label-tag">// 05 BOM</div>
            <div className="label-tag">// VALIDATION REPORT</div>
            <div className="label-tag !text-[#00E5FF]">// library</div>
            <div className="label-tag !text-[#FFCC00]">// csv import</div>
            <CopyChip value=".label-tag" />
          </div>
        </Section>

        <Section title="Tables" slug="tables">
          <div className="border border-white/10 overflow-hidden">
            <table className="w-full text-sm font-mono">
              <thead className="bg-[#1A1F29]">
                <tr className="text-[10px] uppercase tracking-[0.2em] text-gray-400 text-left">
                  <th className="p-3">Part #</th><th className="p-3">Manufacturer</th><th className="p-3">Qty</th><th className="p-3 text-right">Ext $</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ["CL24-4-AL",  "ABB Cope", 2, 286.40],
                  ["B-2A-18-4-AL", "Eaton B-Line", 1, 89.10],
                  ["NSX100F",    "Schneider", 4, 1140.00],
                ].map(([sku, mfr, qty, ext]) => (
                  <tr key={sku} className="border-t border-white/5">
                    <td className="p-3 text-[#00E5FF]">{sku}</td>
                    <td className="p-3">{mfr}</td>
                    <td className="p-3">{qty}</td>
                    <td className="p-3 text-right">${ext.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="bg-[#00E5FF] text-black font-display font-bold">
                  <td className="p-3" colSpan={3}>TOTAL</td><td className="p-3 text-right">$1,515.50</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Section>

        <Section title="Validation report" slug="validation-report">
          <aside className="max-w-md border border-white/10 bg-[#0a0d12] p-4 font-mono text-sm">
            <div className="label-tag mb-3">// VALIDATION REPORT</div>
            <div className="text-xs uppercase tracking-[0.2em] mb-3 font-bold text-[#FF3B5C] inline-flex items-center gap-1">
              <XCircle weight="fill" size={14} /> NON-COMPLIANT
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs mb-3">
              <div className="border border-white/10 p-2"><div className="text-gray-500">Placed</div><div className="text-white font-display text-lg">7</div></div>
              <div className="border border-white/10 p-2"><div className="text-gray-500">Unplaced</div><div className="text-white font-display text-lg">2</div></div>
              <div className="border border-red-500/30 p-2"><div className="text-red-300">Errors</div><div className="text-red-300 font-display text-lg">1</div></div>
              <div className="border border-yellow-500/30 p-2"><div className="text-yellow-300">Warnings</div><div className="text-yellow-300 font-display text-lg">2</div></div>
            </div>
            <ul className="space-y-2 text-xs">
              {MOCK_REPORT.issues.map((i) => (
                <li key={i.code + (i.tag || "")} className={`border-l-2 pl-2 ${i.level === "error" ? "border-[#FF3B5C] text-red-200" : i.level === "warning" ? "border-[#FFCC00] text-yellow-200" : "border-[#3C82B4] text-cyan-200"}`}>
                  <div className="flex items-center gap-1">
                    {i.level === "error" ? <XCircle size={12} weight="fill" /> : i.level === "warning" ? <WarningCircle size={12} weight="fill" /> : <CheckCircle size={12} />}
                    <span className="font-bold">{i.code}</span>
                  </div>
                  <div>{i.message}</div>
                </li>
              ))}
            </ul>
          </aside>
        </Section>

        <Section title="DIM mode colour scale" slug="dim-colors">
          <p className="text-sm text-gray-400 mb-4 max-w-2xl">
            Used by the 3D <span className="font-mono text-[#00E5FF]">DimensionOverlay</span> + side-panel <span className="font-mono text-[#00E5FF]">DistanceEditor</span> to colour-code clearance lines.
            Each direction has a distinct hue; floor/roof use warm/cool to distinguish vertical bands.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {Object.entries(DIM_COLORS).map(([k, hex]) => (
              <div key={k} className="border border-white/10 p-3 flex items-center gap-3 font-mono text-xs" data-testid={`dim-${k}`}>
                <div className="w-10 h-10 shrink-0" style={{ background: hex }} />
                <div className="min-w-0">
                  <div className="text-white font-bold uppercase tracking-[0.18em]">{k}</div>
                  <CopyChip value={hex} kind="hex" />
                </div>
              </div>
            ))}
          </div>
        </Section>

        <Section title="Decorative patterns" slug="patterns">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <CopyChip value=".glow-cyan" />
              <div className="glow-cyan border border-[#00E5FF]/20 p-6 mt-2 text-sm text-gray-300">Used for hero CTAs and selected cards.</div>
            </div>
            <div>
              <CopyChip value=".shimmer-line" />
              <div className="shimmer-line h-px bg-white/5 mt-2" />
              <div className="text-xs text-gray-500 font-mono mt-2">animated divider</div>
            </div>
            <div>
              <CopyChip value=".bp-grid" />
              <div className="bp-grid h-32 border border-white/10 mt-2" />
            </div>
            <div>
              <CopyChip value=".bp-grid-strong" />
              <div className="bp-grid-strong h-32 border border-white/10 mt-2" />
            </div>
            <div className="md:col-span-2 flex items-center gap-3">
              <div className="pulse-dot bg-[#00E5FF]" />
              <CopyChip value=".pulse-dot" />
              <span className="text-xs font-mono text-gray-500">used for live status indicators (validation, hot reload)</span>
            </div>
          </div>
        </Section>

        <footer className="border-t border-white/10 pt-6 mt-12 text-xs font-mono text-gray-500">
          17 custom CSS classes · 0 phantom references · last audited {new Date().toISOString().slice(0, 10)}
        </footer>
      </main>
    </div>
  );
}
